/**
 * BIP-39 mnemonic validation and secp256k1 signature verification, on the
 * audited noble/scure libraries.
 */

import * as secp256k1 from "@noble/secp256k1";
import { sha256 } from "@noble/hashes/sha2.js";
import { hmac } from "@noble/hashes/hmac.js";
import { bytesToHex, hexToBytes } from "@noble/hashes/utils.js";
import * as bip39 from "@scure/bip39";
import { wordlist as englishWordlist } from "@scure/bip39/wordlists/english.js";

// @noble/secp256k1 v3 needs its hashes supplied before its synchronous API works.
secp256k1.hashes.sha256 = (message: Uint8Array): Uint8Array => sha256(message);
secp256k1.hashes.hmacSha256 = (key: Uint8Array, message: Uint8Array): Uint8Array =>
  hmac(sha256, key, message);

export function validateMnemonic(mnemonic: string): boolean {
  return bip39.validateMnemonic(mnemonic, englishWordlist);
}

/**
 * Whether `signatureHex` (64-byte compact) is `publicKeyHex`'s signature over
 * the SHA-256 of `message`.
 */
export function verifySignature(
  message: string,
  signatureHex: string,
  publicKeyHex: string,
): boolean {
  try {
    const messageHash = sha256(new TextEncoder().encode(message));
    return secp256k1.verify(hexToBytes(signatureHex), messageHash, hexToBytes(publicKeyHex), {
      prehash: false,
    });
  } catch {
    return false;
  }
}

/** A secp256k1 public key in compressed hex form, or null if it is not one. */
export function compressedPublicKey(publicKeyHex: string): string | null {
  try {
    return secp256k1.Point.fromHex(publicKeyHex).toHex(true);
  } catch {
    return null;
  }
}

export { bytesToHex, sha256 };
